import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Eye } from "lucide-react";

export default function AdminNewsletter() {
  const [draft, setDraft] = useState({
    subject: "", body: "",
    cadence: "", favorite_sport: "", preferred_brand: "",
  });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [past, setPast] = useState([]);
  const [sports, setSports] = useState([]);
  const [brands, setBrands] = useState([]);

  const loadPast = useCallback(async () => {
    try { setPast((await api.get("/admin/newsletters")).data); } catch {}
  }, []);

  useEffect(() => {
    loadPast();
    api.get("/filters?kind=sport").then((r) => setSports(r.data)).catch(() => {});
    api.get("/filters?kind=brand").then((r) => setBrands(r.data)).catch(() => {});
  }, [loadPast]);

  const upd = (k, v) => setDraft({ ...draft, [k]: v });

  const payload = () => ({
    subject: draft.subject,
    body: draft.body,
    cadence: draft.cadence || null,
    favorite_sport: draft.favorite_sport || null,
    preferred_brand: draft.preferred_brand || null,
  });

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/newsletter/preview", payload());
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (!window.confirm(
      "This will email every matching opted-in customer. Continue?"
    )) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send", payload());
      const failed = r.data.failed_count || 0;
      if (failed) {
        toast.success(`Sent to ${r.data.sent_count} · ${failed} failed`);
      } else {
        toast.success(`Sent to ${r.data.sent_count} recipient${r.data.sent_count === 1 ? "" : "s"}`);
      }
      setDraft({ subject: "", body: "", cadence: "", favorite_sport: "", preferred_brand: "" });
      setPreview(null);
      loadPast();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Newsletter</h1>
      <p className="text-zinc-400 text-sm mb-6">Compose an email and target it to opted-in customers by cadence, favorite sport, or preferred brand. Sends live via Resend from <span className="text-white">noreply@cardlobster.com</span>.</p>

      <div className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] space-y-4">
        <div>
          <label className="text-label text-zinc-500">Subject</label>
          <input value={draft.subject} onChange={(e) => upd("subject", e.target.value)}
                 data-testid="newsletter-subject" className="input mt-1" />
        </div>
        <div>
          <label className="text-label text-zinc-500">Body</label>
          <textarea rows={10} value={draft.body} onChange={(e) => upd("body", e.target.value)}
                    data-testid="newsletter-body" className="input mt-1" />
        </div>
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
          <div>
            <label className="text-label text-zinc-500">Filter · Cadence</label>
            <select value={draft.cadence} onChange={(e) => upd("cadence", e.target.value)}
                    data-testid="newsletter-cadence" className="input mt-1">
              <option value="">All opted-in</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="text-label text-zinc-500">Filter · Favorite Sport</label>
            <select value={draft.favorite_sport} onChange={(e) => upd("favorite_sport", e.target.value)}
                    data-testid="newsletter-sport" className="input mt-1">
              <option value="">Any</option>
              {sports.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-label text-zinc-500">Filter · Preferred Brand</label>
            <select value={draft.preferred_brand} onChange={(e) => upd("preferred_brand", e.target.value)}
                    data-testid="newsletter-brand" className="input mt-1">
              <option value="">Any</option>
              {brands.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 pt-4 border-t border-white/10">
          <button onClick={runPreview} disabled={previewing} className="btn-outline text-sm" data-testid="newsletter-preview-btn">
            <Eye size={14} className="inline mr-2 -mt-0.5" /> {previewing ? "Checking…" : "Preview Recipients"}
          </button>
          <button onClick={send} disabled={sending} className="btn-primary text-sm" data-testid="newsletter-send-btn">
            <Send size={14} className="inline mr-2 -mt-0.5" /> {sending ? "Sending…" : "Send Newsletter"}
          </button>
        </div>

        {preview && (
          <div className="border border-white/10 rounded p-4 mt-4" data-testid="newsletter-preview-result">
            <div className="text-label text-zinc-500">Preview</div>
            <div className="font-display text-3xl mt-1">{preview.recipient_count} recipient{preview.recipient_count === 1 ? "" : "s"}</div>
            {preview.sample_recipients?.length > 0 && (
              <ul className="mt-4 text-xs text-zinc-400 space-y-1 max-h-48 overflow-y-auto">
                {preview.sample_recipients.map((r) => (
                  <li key={r.email}>{r.name} · <span className="text-zinc-500">{r.email}</span></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <h2 className="font-display text-2xl mt-10 mb-4">Past Newsletters ({past.length})</h2>
      {past.length === 0 && <div className="text-zinc-500 text-sm">No newsletters sent yet.</div>}
      <div className="space-y-3" data-testid="newsletter-past-list">
        {past.map((n) => (
          <div key={n.id} className="border border-white/10 rounded p-4 bg-[var(--cl-surface)]">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium">{n.subject}</div>
                <div className="text-xs text-zinc-500 mt-1">{n.sent_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-zinc-500">Recipients</div>
                <div className="font-display text-xl">{n.recipient_count}</div>
                <div className="text-[10px] text-[var(--cl-orange)] uppercase tracking-widest mt-1">{n.status}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-400 mt-3 line-clamp-2">{n.body}</div>
          </div>
        ))}
      </div>

      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}
